import { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { Check, Code2, Server } from 'lucide-react';
import { useAccount } from 'wagmi';
import { TARGET_CHAIN } from '../web3/config';
import { getDeployment } from '../web3/contracts';
import { db } from '../web3/firebase';
import TrustBar from './TrustBar';

const TIERS = [
  {
    name: 'Starter',
    price: '무료',
    features: ['공개 컨트랙트 직접 호출', '커뮤니티 지원', '레이트 리밋: RPC 제공자 기준'],
    highlighted: false,
  },
  {
    name: 'Growth',
    price: '$1,000/월',
    features: ['전용 RPC 엔드포인트', 'Swap·Bridge·Prediction API', '기술 지원 (영업일 24h)', 'SLA 99.5%'],
    highlighted: true,
  },
  {
    name: 'Enterprise',
    price: '맞춤 견적 (~$100,000/월)',
    features: ['화이트라벨 구축', '전용 인프라 + 커스텀 SLA', 'RWA·유동성 컨설팅', '전담 엔지니어 배정'],
    highlighted: false,
  },
];

const CODE_EXAMPLE = `import { createPublicClient, http, parseAbi } from 'viem';
import { bscTestnet } from 'viem/chains';

const client = createPublicClient({ chain: bscTestnet, transport: http() });

// Pelagos Router — 실시간 스왑 견적
const quote = await client.readContract({
  address: 'ROUTER_ADDRESS',
  abi: parseAbi([
    'function getAmountsOut(uint256,address[]) view returns (uint256[])',
  ]),
  functionName: 'getAmountsOut',
  args: [10n ** 18n, ['TOKEN_IN', 'TOKEN_OUT']],
});`;

export default function EnterpriseApiView() {
  const { chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);

  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [usage, setUsage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const submit = async () => {
    if (!company.trim() || !email.trim()) return;
    setStatus('sending');
    try {
      await addDoc(collection(db, 'apiLeads'), {
        company: company.trim(),
        email: email.trim(),
        usage: usage.trim(),
        createdAt: serverTimestamp(),
      });
      setStatus('sent');
      setCompany('');
      setEmail('');
      setUsage('');
    } catch {
      setStatus('error');
    }
  };

  const endpoints: { label: string; address: string | undefined }[] = [
    { label: 'Router (Swap)', address: deployment?.router },
    { label: 'Factory', address: deployment?.factory },
    { label: 'Farm', address: deployment?.farm },
    { label: 'Earn', address: deployment?.earn?.address },
    { label: 'Salt RWA', address: deployment?.saltRwa?.token },
    { label: 'Launchpad', address: deployment?.launchpad?.address },
    { label: 'Staking', address: deployment?.staking?.address },
    { label: 'Lending', address: deployment?.lending?.address },
    { label: 'Prediction', address: deployment?.prediction?.address },
    { label: 'Bridge', address: deployment?.bridge?.address },
  ];

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-abyss-850 border border-abyss-700 flex items-center justify-center text-tide-400">
          <Server size={20} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foam-100">Enterprise API</h2>
          <p className="text-xs text-foam-500 mt-0.5">
            Pelagos의 스왑·브릿지·예측시장·RWA 인프라를 기업 시스템에 연동하세요
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {TIERS.map((tier) => (
          <div
            key={tier.name}
            className={`rounded-xl border p-5 flex flex-col gap-3 ${
              tier.highlighted ? 'border-tide-400/50 bg-tide-400/5' : 'border-abyss-700 bg-abyss-850'
            }`}
          >
            <div>
              <div className="text-sm font-semibold text-foam-100">{tier.name}</div>
              <div className={`text-xl font-bold mt-1 ${tier.highlighted ? 'text-tide-400' : 'text-foam-100'}`}>
                {tier.price}
              </div>
            </div>
            <ul className="space-y-1.5 text-xs text-foam-300 flex-1">
              {tier.features.map((f) => (
                <li key={f} className="flex gap-1.5 items-start">
                  <Check size={12} className="text-tide-400 mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-abyss-700 bg-abyss-850 p-4">
          <h3 className="text-sm font-semibold text-foam-100 mb-3 flex items-center gap-1.5">
            <Code2 size={14} className="text-tide-400" />
            공개 컨트랙트 엔드포인트 (BSC Testnet)
          </h3>
          <div className="space-y-1.5 text-[11px]">
            {endpoints
              .filter((e) => e.address)
              .map((e) => (
                <div key={e.label} className="flex justify-between gap-3 py-1 border-b border-abyss-700/60">
                  <span className="text-foam-500 shrink-0">{e.label}</span>
                  <code className="text-foam-300 truncate tabular-nums">{e.address}</code>
                </div>
              ))}
          </div>
          <pre className="mt-3 p-3 rounded-lg bg-abyss-900 border border-abyss-700 text-[10px] text-foam-300 overflow-x-auto leading-relaxed">
            {CODE_EXAMPLE}
          </pre>
        </div>

        <div className="rounded-xl border border-abyss-700 bg-abyss-850 p-4">
          <h3 className="text-sm font-semibold text-foam-100 mb-1">도입 문의</h3>
          <p className="text-[11px] text-foam-500 mb-3">
            전용 엔드포인트, SLA, 화이트라벨이 필요하시면 남겨주세요. 영업일 기준 24시간 내 회신합니다.
          </p>
          <div className="space-y-2">
            <input
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="회사명 *"
              className="w-full px-3 py-2.5 rounded-lg border border-abyss-700 bg-abyss-900/60 text-xs text-foam-100 outline-none placeholder:text-foam-700"
            />
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="업무용 이메일 *"
              type="email"
              className="w-full px-3 py-2.5 rounded-lg border border-abyss-700 bg-abyss-900/60 text-xs text-foam-100 outline-none placeholder:text-foam-700"
            />
            <textarea
              value={usage}
              onChange={(e) => setUsage(e.target.value)}
              placeholder="어떤 용도로 사용하실 예정인가요? (예: 자사 앱에 스왑 기능 내장)"
              rows={4}
              className="w-full px-3 py-2.5 rounded-lg border border-abyss-700 bg-abyss-900/60 text-xs text-foam-100 outline-none placeholder:text-foam-700 resize-none"
            />
            <button
              onClick={submit}
              disabled={!company.trim() || !email.trim() || status === 'sending'}
              className="w-full py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed bg-tide-500 text-abyss-950 hover:bg-tide-400"
            >
              {status === 'sending' ? '전송 중...' : '문의 보내기'}
            </button>
            {status === 'sent' && (
              <div className="px-3 py-2 rounded-md bg-tide-400/10 text-tide-400 text-xs">
                문의가 접수되었습니다. 곧 연락드리겠습니다.
              </div>
            )}
            {status === 'error' && (
              <div className="px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs">
                전송에 실패했습니다. 잠시 후 다시 시도해주세요.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-3 rounded-lg border border-abyss-700 bg-abyss-850 text-[11px] text-foam-500 leading-relaxed">
        현재는 BSC 테스트넷 공개 컨트랙트 기준 안내입니다. 유료 요금제(전용 RPC, SLA, 화이트라벨)는 메인넷
        출시와 함께 제공 예정이며, 위 가격은 사업계획 기준 예상 가격입니다.
      </div>
      <TrustBar />
    </div>
  );
}
